import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClipboardPlus, Copy } from 'lucide-react';
import CustomAlert from '../CustomAlert';

const apiUrl = process.env.REACT_APP_API_URL;

const EMPTY = {
  name: '',
  price: '',
  cost_price: '',
  tax: '18',
  quantity: '',
  type: '',
  discount: '',
  discount_percentage: '',
  description: '',
  unit: 'пар',
  location: '',
  code: '',
  category_id: '',
  manufacturer_id: '',
};

/**
 * Product edit form. Same shape as AddProduct: no page chrome, so it sits
 * either in the Drawer on the list page or standalone on /products/edit/:id.
 *
 * id — from props in the drawer, from the route param standalone
 * onSuccess(updated) — drawer closes and the list refreshes
 */
export default function ProductEdit({ id: idProp, onSuccess }) {
  const params = useParams();
  const id = idProp ?? params.id;
  const navigate = useNavigate();

  const [product, setProduct] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setAlert({ show: true, message: 'Шифрата е копирана!', type: 'success' });
    } catch (err) {
      console.error('Failed to copy: ', err);
      setAlert({ show: true, message: 'Неуспешно копирање на шифрата', type: 'error' });
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');

    // Load categories
    fetch(`${apiUrl}/categories`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then(setCategories)
      .catch((err) => {
        console.error('Failed to load categories:', err);
        setAlert({ show: true, message: 'Неуспешно вчитување на категории', type: 'error' });
      });

    // Load manufacturers
    fetch(`${apiUrl}/manufacturer`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then(setManufacturers)
      .catch((err) => {
        console.error('Failed to load manufacturers:', err);
        setAlert({ show: true, message: 'Неуспешно вчитување на производители', type: 'error' });
      });
  }, []);

  // Fetch product by ID on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${apiUrl}/products/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch product');
        return res.json();
      })
      // NULL columns would flip the inputs to uncontrolled — keep '' for those.
      .then((data) =>
        setProduct({
          ...EMPTY,
          ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== null)),
        })
      )
      .catch((err) => {
        console.error(err);
        setAlert({ show: true, message: 'Неуспешно вчитување на производот', type: 'error' });
      });
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!product.name || !product.price || !product.tax) {
      setAlert({ show: true, message: 'Пополнете ги сите задолжителни полиња.', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/products/edit/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(product),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to update product');

      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate('/products/list');
      }
    } catch (error) {
      console.error(error);
      setAlert({ show: true, message: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Same field spec as AddProduct — the add and edit drawers sit on the same
  // list page, so they should not look like different applications. Plain
  // functions, NOT components: a component declared inside render would
  // remount the input on every keystroke and drop focus.
  const LABEL = 'block text-sm font-medium text-gray-700';
  const INPUT =
    'mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-600';

  const renderField = ({ label, name, type = 'text', required = false, span, placeholder }) => (
    <div key={name} className={span}>
      <label className={LABEL}>
        {label}
        {required && ' *'}
      </label>
      <input
        type={type}
        name={name}
        value={product[name]}
        onChange={handleChange}
        required={required}
        placeholder={placeholder}
        className={INPUT}
      />
    </div>
  );

  const renderSelect = ({ label, name, required = false, children }) => (
    <div key={name}>
      <label className={LABEL}>
        {label}
        {required && ' *'}
      </label>
      <select
        name={name}
        value={product[name]}
        onChange={handleChange}
        required={required}
        className={INPUT}
      >
        {children}
      </select>
    </div>
  );

  return (
    <>
      <CustomAlert
        isOpen={alert.show}
        type={alert.type}
        title={alert.type === 'success' ? 'Успешно' : 'Грешка'}
        message={alert.message}
        onClose={() => setAlert({ show: false, message: '', type: 'success' })}
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Required fields */}
        <div className="flex items-center justify-between gap-3">
          <div className="border-l-4 border-green-500 pl-3">
            <h2 className="text-base font-semibold text-gray-800">Основни податоци</h2>
          </div>

          {product.code && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => copyToClipboard(product.code)}
                title="Кликнете за копирање"
                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg font-mono text-sm flex items-center gap-2 group hover:bg-blue-200 transition-colors"
              >
                {product.code}
                <Copy size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <div
                className={`px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 ${
                  product.import ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}
                title={product.import ? 'Овој производ е увезен' : 'Овој производ е внесен рачно'}
              >
                <ClipboardPlus size={12} />
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
          {renderField({
            label: 'Назив на производ',
            name: 'name',
            required: true,
            span: 'sm:col-span-2 lg:col-span-3',
            placeholder: 'Внесете назив',
          })}
          {renderField({
            label: 'Продажна цена',
            name: 'price',
            type: 'number',
            required: true,
            placeholder: '0.00',
          })}
          {renderField({
            label: 'Набавна цена',
            name: 'cost_price',
            type: 'number',
            placeholder: '0.00',
          })}
          {renderSelect({
            label: 'Данок',
            name: 'tax',
            required: true,
            children: (
              <>
                <option value="0">0%</option>
                <option value="5">5%</option>
                <option value="18">18%</option>
              </>
            ),
          })}
          {renderSelect({
            label: 'Единица мерка',
            name: 'unit',
            children: (
              <>
                <option value="пар">пар</option>
                <option value="kg">kg</option>
                <option value="m">m</option>
              </>
            ),
          })}
          {renderSelect({
            label: 'Категорија',
            name: 'category_id',
            children: (
              <>
                <option value="">Изберете категорија</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </>
            ),
          })}
          {renderSelect({
            label: 'Производител',
            name: 'manufacturer_id',
            children: (
              <>
                <option value="">Изберете производител</option>
                {manufacturers.map((manufacturer) => (
                  <option key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.name}
                  </option>
                ))}
              </>
            ),
          })}
        </div>

        {/* Optional fields */}
        <div className="border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-semibold focus:outline-none transition-colors"
          >
            <span className="mr-2">{showMore ? '▼' : '▶'}</span>
            {showMore ? 'Сокриј дополнителни полиња' : 'Прикажи дополнителни полиња'}
          </button>

          {showMore && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 mt-3">
              {renderField({ label: 'Количина', name: 'quantity', type: 'number', placeholder: '0' })}
              {renderField({ label: 'Тип', name: 'type', placeholder: 'Тип на производ' })}
              {renderField({ label: 'Локација', name: 'location', placeholder: 'Локација во магацин' })}
              {renderField({ label: 'Попуст', name: 'discount', type: 'number', placeholder: '0.00' })}
              {renderField({
                label: 'Попуст %',
                name: 'discount_percentage',
                type: 'number',
                placeholder: '0',
              })}

              <div className="sm:col-span-2 lg:col-span-3">
                <label className={LABEL}>Опис</label>
                <textarea
                  name="description"
                  value={product.description}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Опис на производот..."
                  className={`${INPUT} resize-none`}
                />
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="w-full px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? 'Се зачувува...' : 'Зачувај промени'}
          </button>
        </div>
      </form>
    </>
  );
}
